import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { CreditCard, Smartphone, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PREMIUM_TZS = 15000;

// Normalize whatever the user types into +255XXXXXXXXX format for Selcom.
const normalizeTzPhone = (raw) => {
  if (!raw) return '';
  let s = String(raw).replace(/[^\d+]/g, '');
  if (s.startsWith('+')) return s;
  if (s.startsWith('255')) return `+${s}`;
  if (s.startsWith('0') && s.length === 10) return `+255${s.slice(1)}`;
  if (s.length === 9) return `+255${s}`;
  return s;
};

export default function SubscribePremiumDialog({ open, onOpenChange }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [phone, setPhone] = useState(user?.phone || '');
  const [submitting, setSubmitting] = useState(false);

  const authed = Boolean(token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authed) {
      // Stash the intent so /register can bounce them back here after signup.
      sessionStorage.setItem('wekeza_intent', 'subscribe_premium');
      onOpenChange(false);
      navigate('/register');
      return;
    }
    const normalized = normalizeTzPhone(phone);
    if (!normalized || normalized.length < 12) {
      toast.error(t('Enter a valid Tanzania mobile number', 'Ingiza namba sahihi ya simu ya Tanzania'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/payments/selcom/subscribe-premium`,
        { phone: normalized },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { checkout_url, simulated } = res.data || {};
      if (!checkout_url) {
        toast.error(t('Payment gateway did not return a URL', 'Selcom haikurudisha URL'));
        return;
      }
      if (simulated) {
        toast.info(
          t('Selcom is in simulated mode on this environment. Redirecting to the demo checkout.',
            'Selcom iko katika hali ya majaribio. Tunakupeleka kwenye ukurasa wa mfano.'),
        );
      } else {
        toast.success(t('Redirecting to Selcom...', 'Tunakupeleka Selcom...'));
      }
      // Give the toast a beat, then hand off to the Selcom gateway.
      setTimeout(() => { window.location.href = checkout_url; }, 700);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === 'string'
          ? msg
          : t('Could not start the Selcom checkout. Please try again.',
              'Tumeshindwa kuanza malipo. Jaribu tena.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="premium-subscribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-700" />
            {t('Upgrade to Premium', 'Panda daraja hadi Premium')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'TZS 15,000 / month via Selcom mobile money. Cancel anytime.',
              'TZS 15,000 kwa mwezi kupitia Selcom. Sitisha muda wowote.',
            )}
          </DialogDescription>
        </DialogHeader>

        {!authed ? (
          <div className="space-y-3 py-2 text-sm text-slate-700">
            <p>
              {t(
                'Create a free account first, then complete the upgrade in one tap.',
                'Fungua akaunti bure, kisha malizia kupanda daraja mara moja.',
              )}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="premium-phone" className="block text-sm font-medium text-slate-800 mb-1.5">
                {t('Mobile money number', 'Namba ya simu ya malipo')}
              </label>
              <div className="relative">
                <Smartphone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="premium-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0754 710 139"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100 text-sm"
                  data-testid="premium-phone-input"
                  required
                />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                {t(
                  'You will approve the TZS 15,000 charge on your phone via Selcom.',
                  'Utaidhinisha malipo ya TZS 15,000 kwenye simu yako kupitia Selcom.',
                )}
              </p>
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-xs text-emerald-800">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {t(
                  'Selcom is a licensed Tanzanian payment gateway. Wekeza-Smart never sees your PIN.',
                  'Selcom ni mtoa huduma wa malipo wa Tanzania. Wekeza-Smart haioni PIN yako.',
                )}
              </span>
            </div>
          </form>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="premium-cancel-btn">
            {t('Not now', 'Sio sasa')}
          </Button>
          {authed ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="premium-pay-btn"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('Starting Selcom...', 'Inaanzisha Selcom...')}</>
                : t(`Pay TZS ${PREMIUM_TZS.toLocaleString()}`, `Lipa TZS ${PREMIUM_TZS.toLocaleString()}`)}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="premium-create-account-btn"
            >
              {t('Create free account', 'Fungua akaunti bure')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
